import { c as _c } from "react/compiler-runtime";
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { ItemCard, ItemCardBody, ItemCardDivider, ItemCardFooter, ItemCardMeta, ItemCardSection, ItemCardSummary } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import '@sola/locales/formatters.ts';
import { GraphQLPledge } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { PledgeIcon } from '../../atoms/pledge-icon/pledge-icon.tsx';
import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet.tsx';
import { formatPledgeFooter, formatPledgeName } from '../../utils/pledges.ts';
export type PledgeCardProps = {
  pledge: GraphQLPledge;
  isActive?: boolean;
  onActiveChange?: (open: boolean, commitmentId: string) => void;
};
const useComponentData = (args: PledgeCardProps & {
  t: TFunction;
}) => {
  const {
    pledge,
    t
  } = args;
  return {
    ...(pledge.pledgeName && {
      title: formatPledgeName(pledge.pledgeName, t)
    }),
    label: t('my:commitments.pledges.subTitle'),
    rate: t('core:numbers.currencyCustom', {
      amount: pledge.totalPledgeAmount
    }),
    footer: formatPledgeFooter(t, pledge.nextInstallmentDate)
  };
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const PledgeCard: React.FC<PledgeCardProps> = props => {
  const $ = _c(33);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let t1;
  if ($[1] !== props || $[2] !== t) {
    t1 = {
      t,
      ...props
    };
    $[1] = props;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cd = useComponentData(t1);
  const {
    pledge,
    isActive,
    onActiveChange
  } = props;
  let t2;
  if ($[4] !== pledge) {
    t2 = <PledgeIcon {...pledge} />;
    $[4] = pledge;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== cd.label || $[7] !== cd.title || $[8] !== t2) {
    t3 = <ItemCardSummary icon={t2} title={cd.title} subTitle={cd.label} />;
    $[6] = cd.label;
    $[7] = cd.title;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== cd.rate) {
    t4 = <ItemCardMeta text={cd.rate} />;
    $[10] = cd.rate;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <ItemCardSection><ItemCardBody>{t3}{t4}</ItemCardBody></ItemCardSection>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ItemCardDivider />;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== cd.footer) {
    t7 = <span className="label-moderate label-neutral-mild label-sm md:label-md">{cd.footer}</span>;
    $[16] = cd.footer;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("my:commitments.detailsSheet.triggerLabel");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <div className="ml-auto"><LinkStyle noUnderline={true} noPadding={true} inline={true} variant="primary" size="lg"><button type="button" className="CommitmentCard__trigger">{t8}</button></LinkStyle></div>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== isActive || $[23] !== onActiveChange || $[24] !== pledge || $[25] !== t9) {
    t10 = <CommitmentDetailsSheet pledge={pledge} isOpen={isActive} onOpenChange={onActiveChange} openTrigger={t9} />;
    $[22] = isActive;
    $[23] = onActiveChange;
    $[24] = pledge;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t7) {
    t11 = <ItemCardFooter justify="between">{t7}{t10}</ItemCardFooter>;
    $[27] = t10;
    $[28] = t7;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t5) {
    t12 = <ItemCard>{t5}{t6}{t11}</ItemCard>;
    $[30] = t11;
    $[31] = t5;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
};